/**
 * Cart drawer (Dawn-style).
 *
 * Twee custom elements werken samen:
 *
 *   <cart-drawer>          Drawer-chrome: open/close, backdrop, header,
 *                          footer. Luistert naar globale cart-events en
 *                          ververst de inner section-HTML via de Section
 *                          Rendering API.
 *
 *   <cart-drawer-items>    Lijst met line items. Klik op +/- of "Verwijder"
 *                          stuurt naar `/cart/change.js?sections=cart-drawer`,
 *                          die in één response zowel de cart-mutatie als de
 *                          verse HTML van de section teruggeeft. Die HTML
 *                          wordt vervolgens via `applySectionHTML()` ge-swap't
 *                          in de drawer.
 *
 * Belangrijkste verbeteringen t.o.v. de vorige implementatie:
 *   - Eén API-call per actie i.p.v. twee sequentiële fetches.
 *   - Server-rendered HTML (Liquid) i.p.v. JS string templating; prijzen en
 *     totalen blijven 100% consistent met de PDP/cart-page.
 *   - Per-line loading state — andere regels blijven interactief.
 *   - Scrollpositie van de items-lijst blijft behouden tussen swaps.
 *   - Close-buttons + backdrop worden opnieuw bekabeld na elke swap, omdat
 *     de innerHTML-vervanging ze als nieuwe DOM-nodes terugbrengt.
 *
 * Markup verwacht: zie `sections/cart-drawer.liquid` en
 * `snippets/cart-drawer-items.liquid`.
 */

class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this._onOpenButtonClick = this._onOpenButtonClick.bind(this);
    this._onCloseClick = this._onCloseClick.bind(this);
    this._onBackdropClick = this._onBackdropClick.bind(this);
    this._onDocumentKeydown = this._onDocumentKeydown.bind(this);
    this._onExternalCartChange = this._onExternalCartChange.bind(this);
    this._onPanelTransitionEnd = this._onPanelTransitionEnd.bind(this);
    this._refreshing = false;
  }

  connectedCallback() {
    this.autoOpenOnAdd = this.hasAttribute('auto-open-on-add');
    this._wireListeners();
    // Initial badge-sync uit de server-rendered data-attribuut.
    const initialCount = parseInt(this.getAttribute('data-cart-item-count'), 10);
    if (!isNaN(initialCount)) this._updateBadge(initialCount);
  }

  disconnectedCallback() {
    this._unwireListeners();
  }

  /* --------------------------------- Public API ------------------------- */

  open() {
    this._setOpen(true);
  }

  close() {
    this._setOpen(false);
  }

  /**
   * Re-fetch section-HTML en swap de inner content. Gebruikt voor
   * EXTERNE cart-veranderingen (quick-add vanaf een product card, app die
   * /cart/add aanroept, ander tabblad via subscribe shim, etc).
   */
  async refresh() {
    if (this._refreshing) return;
    this._refreshing = true;
    try {
      const url = `${window.location.pathname}?sections=${encodeURIComponent(this.sectionId)}`;
      const res = await fetch(url, { credentials: 'same-origin' });
      if (!res.ok) throw new Error(`Section fetch failed: ${res.status}`);
      const data = await res.json();
      const html = data[this.sectionId];
      if (html) this.applySectionHTML(html, { silent: false });
    } catch (e) {
      console.error('[cart-drawer] refresh failed', e);
    } finally {
      this._refreshing = false;
    }
  }

  /**
   * Vervang de inner content van deze drawer met verse section-HTML.
   * Wordt aangeroepen door <cart-drawer-items> na een succesvolle change-call
   * waarbij de Section Rendering API de nieuwe HTML al heeft meegeleverd —
   * geen extra round-trip nodig.
   */
  applySectionHTML(html, options = {}) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const newDrawer = doc.querySelector('cart-drawer');
    if (!newDrawer) return;

    // Behoud scrollpositie van de items-lijst over de swap heen.
    const oldItems = this.querySelector('[data-cart-drawer-items]');
    const scrollTop = oldItems ? oldItems.scrollTop : 0;

    this.innerHTML = newDrawer.innerHTML;

    const newItems = this.querySelector('[data-cart-drawer-items]');
    if (newItems) newItems.scrollTop = scrollTop;

    // Sync outer-element attributes die styling/state beïnvloeden.
    const newCount = newDrawer.getAttribute('data-cart-item-count');
    if (newCount !== null) {
      this.setAttribute('data-cart-item-count', newCount);
      const count = parseInt(newCount, 10);
      if (!isNaN(count)) this._updateBadge(count);
    }
    const isEmpty = newDrawer.classList.contains('cart-is-empty');
    this.classList.toggle('cart-is-empty', isEmpty);

    const itemsEl = this.querySelector('[data-cart-drawer-items]');
    if (itemsEl) {
      itemsEl.classList.toggle('cart-drawer-items--empty', isEmpty);
    }

    // Close/backdrop zijn vervangen — opnieuw bekabelen.
    this._wireScopedListeners();

    if (!options.silent) this._publishUpdate();
  }

  /* --------------------------------- Internals -------------------------- */

  get sectionId() {
    return this.getAttribute('data-section-id') || 'cart-drawer';
  }

  _wireListeners() {
    this.openButtons = document.querySelectorAll('[data-cart-drawer-open]');
    this.openButtons.forEach((btn) => btn.addEventListener('click', this._onOpenButtonClick));

    this._wireScopedListeners();

    document.addEventListener('keydown', this._onDocumentKeydown);
    document.addEventListener('cart:add', this._onExternalCartChange);
    document.addEventListener('cart:update', this._onExternalCartChange);
    if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS) {
      this._unsubscribePub = window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, (payload) => {
        // Voorkom feedback-loop: onze eigen publish triggert niet weer een refresh.
        if (payload && payload.source === 'cart-drawer') return;
        this._onExternalCartChange();
      });
    }
  }

  _unwireListeners() {
    if (this.openButtons) {
      this.openButtons.forEach((btn) => btn.removeEventListener('click', this._onOpenButtonClick));
    }
    if (this._closeButtons) {
      this._closeButtons.forEach((btn) => btn.removeEventListener('click', this._onCloseClick));
    }
    if (this._backdrop) this._backdrop.removeEventListener('click', this._onBackdropClick);
    if (this._panel) this._panel.removeEventListener('transitionend', this._onPanelTransitionEnd);
    document.removeEventListener('keydown', this._onDocumentKeydown);
    document.removeEventListener('cart:add', this._onExternalCartChange);
    document.removeEventListener('cart:update', this._onExternalCartChange);
    if (typeof this._unsubscribePub === 'function') this._unsubscribePub();
  }

  // Re-attach voor close-buttons + backdrop die BINNEN de drawer leven.
  // Wordt aangeroepen na initialisatie én na elke section-swap (omdat die
  // elementen dan vervangen zijn door verse DOM-nodes).
  _wireScopedListeners() {
    if (this._closeButtons) {
      this._closeButtons.forEach((btn) => btn.removeEventListener('click', this._onCloseClick));
    }
    this._closeButtons = this.querySelectorAll('[data-cart-drawer-close]');
    this._closeButtons.forEach((btn) => btn.addEventListener('click', this._onCloseClick));

    if (this._backdrop) this._backdrop.removeEventListener('click', this._onBackdropClick);
    this._backdrop = this.querySelector('[data-cart-drawer-backdrop]');
    if (this._backdrop) this._backdrop.addEventListener('click', this._onBackdropClick);

    if (this._panel) this._panel.removeEventListener('transitionend', this._onPanelTransitionEnd);
    this._panel = this.querySelector('[data-cart-drawer-panel]');
    if (this._panel) this._panel.addEventListener('transitionend', this._onPanelTransitionEnd);
  }

  _setOpen(isOpen) {
    if (isOpen) {
      this.classList.remove('hidden');
      // Reflow zodat de open-animatie altijd triggert na hidden.
      void this.offsetWidth;
      this.classList.add('cart-drawer--open');
    } else {
      this.classList.remove('cart-drawer--open');
    }

    this.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
    if (this.openButtons) {
      this.openButtons.forEach((b) => b.setAttribute('aria-expanded', isOpen ? 'true' : 'false'));
    }
    document.documentElement.classList.toggle('overflow-hidden', isOpen);

    const eventName = isOpen ? 'cart-drawer-open' : 'cart-drawer-close';
    document.dispatchEvent(new CustomEvent(eventName, { detail: { cartDrawer: this } }));
    if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
      const key = isOpen ? window.PUB_SUB_EVENTS.cartDrawerOpen : window.PUB_SUB_EVENTS.cartDrawerClose;
      if (key) window.publish(key, { cartDrawer: this });
    }
  }

  _onPanelTransitionEnd(e) {
    if (e.target !== this._panel) return;
    if (e.propertyName !== 'transform') return;
    if (!this.classList.contains('cart-drawer--open')) {
      this.classList.add('hidden');
    }
  }

  _updateBadge(count) {
    const els = document.querySelectorAll('[data-cart-count], .cart-count-bubble');
    const hasItems = count > 0;
    els.forEach((el) => {
      el.textContent = hasItems ? String(count) : '';
      el.classList.toggle('hidden', !hasItems);
    });
  }

  _publishUpdate() {
    const detail = { source: 'cart-drawer', sectionId: this.sectionId };
    document.dispatchEvent(new CustomEvent('cart:refreshed', { detail }));
    if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
      window.publish(window.PUB_SUB_EVENTS.cartUpdate, detail);
    }
  }

  _onOpenButtonClick() {
    this.open();
  }
  _onCloseClick() {
    this.close();
  }
  _onBackdropClick() {
    this.close();
  }
  _onDocumentKeydown(e) {
    if (e.key === 'Escape' && this.classList.contains('cart-drawer--open')) this.close();
  }

  _onExternalCartChange() {
    // Geen section-refresh tijdens een lopende drawer-mutatie — voorkomt dubbele
    // /cart/change + Section API calls naast Essential Upsell e.d.
    if (CartDrawerItems._mutationPending > 0) return;
    this.refresh().then(() => {
      if (this.autoOpenOnAdd && !this.classList.contains('cart-drawer--open')) this._setOpen(true);
    });
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

/** Shopify line-item keys in selectors; CSS.escape when available. */
function cartDrawerEscapeKey(value) {
  const s = String(value ?? '');
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') return CSS.escape(s);
  return s.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

const CART_FETCH_HEADERS = {
  'Content-Type': 'application/json',
  Accept: 'application/json',
  'X-Requested-With': 'XMLHttpRequest',
};

/* ============================================================================
 * <cart-drawer-items>
 * ========================================================================== */

class CartDrawerItems extends HTMLElement {
  /**
   * Alle +/- / verwijderen-requests lopen seriëel. Parallelle POSTs naar
   * /cart/change.js geven bij Shopify vaak 403 / inconsistent gedrag,
   * zeker als theme-/app-scripts tegelijk /cart.js pollen.
   */
  static _mutationTail = Promise.resolve();
  /** Aantal nog lopende of in de wachtrij staande mutaties (globale “bezig”-state tot 0). */
  static _mutationPending = 0;
  static _appNotifyTimer = null;

  /**
   * Apps (Essential Upsell, enz.) luisteren op cart-update en pollen /cart.js.
   * Eén debounced melding na de laatste mutatie i.p.v. per klik — minder 429 op theme dev.
   */
  static _scheduleAppCartNotify(detail) {
    clearTimeout(CartDrawerItems._appNotifyTimer);
    CartDrawerItems._appNotifyTimer = setTimeout(() => {
      CartDrawerItems._appNotifyTimer = null;
      if (CartDrawerItems._mutationPending > 0) return;
      document.dispatchEvent(new CustomEvent('cart:refreshed', { detail }));
      if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
        window.publish(window.PUB_SUB_EVENTS.cartUpdate, detail);
      }
    }, 500);
  }

  static async _postCartChange(url, body) {
    const opts = {
      method: 'POST',
      credentials: 'same-origin',
      headers: CART_FETCH_HEADERS,
      body: JSON.stringify(body),
    };

    const delays = [0, 1200, 2500];
    let lastRes = null;
    let lastText = '';

    for (let attempt = 0; attempt < delays.length; attempt++) {
      if (delays[attempt] > 0) {
        await new Promise((r) => setTimeout(r, delays[attempt]));
      }
      lastRes = await fetch(url, opts);
      lastText = await lastRes.text();
      if (lastRes.status !== 429) {
        return { res: lastRes, text: lastText };
      }
    }

    return { res: lastRes, text: lastText };
  }

  static _setGlobalMutating(on) {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    drawer.classList.toggle('cart-drawer--mutating', on);
    if (on) drawer.setAttribute('aria-busy', 'true');
    else drawer.removeAttribute('aria-busy');

    const panel = drawer.querySelector('[data-cart-drawer-panel]');
    if (!panel) return;
    panel.querySelectorAll('button').forEach((btn) => {
      if (on) {
        if (btn.dataset.cartDrawerPrevDisabled === undefined) {
          btn.dataset.cartDrawerPrevDisabled = btn.disabled ? '1' : '0';
          btn.disabled = true;
        }
      } else if (btn.dataset.cartDrawerPrevDisabled !== undefined) {
        btn.disabled = btn.dataset.cartDrawerPrevDisabled === '1';
        delete btn.dataset.cartDrawerPrevDisabled;
      }
    });
  }

  static _enqueueMutation(fn) {
    CartDrawerItems._mutationPending += 1;
    CartDrawerItems._setGlobalMutating(true);

    const run = async () => {
      try {
        await fn();
      } finally {
        CartDrawerItems._mutationPending -= 1;
        if (CartDrawerItems._mutationPending <= 0) {
          CartDrawerItems._mutationPending = 0;
          CartDrawerItems._setGlobalMutating(false);
        } else {
          // DOM kan intussen vervangen zijn door Section API; opnieuw disablen voor a11y.
          CartDrawerItems._setGlobalMutating(true);
        }
      }
    };

    const next = CartDrawerItems._mutationTail.then(run, run);
    CartDrawerItems._mutationTail = next.catch(() => {});
    return next;
  }

  constructor() {
    super();
    this._onClick = this._onClick.bind(this);
  }

  connectedCallback() {
    this.addEventListener('click', this._onClick);
    this.cartDrawer = this.closest('cart-drawer');
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._onClick);
  }

  _onClick(e) {
    const target = e.target;
    if (!target) return;

    const hostDrawer = this.closest('cart-drawer');
    if (hostDrawer && hostDrawer.classList.contains('cart-drawer--mutating')) {
      e.preventDefault();
      return;
    }

    const incBtn = target.closest('[data-cart-quantity-increase]');
    if (incBtn) {
      e.preventDefault();
      const item = incBtn.closest('[data-cart-item]');
      if (!item) return;
      const current = this._readQuantity(item);
      this._updateLine(item, current + 1);
      return;
    }

    const decBtn = target.closest('[data-cart-quantity-decrease]');
    if (decBtn) {
      e.preventDefault();
      const item = decBtn.closest('[data-cart-item]');
      if (!item) return;
      const current = this._readQuantity(item);
      // Onder 1 = regel verwijderen.
      this._updateLine(item, Math.max(0, current - 1));
      return;
    }

    const removeBtn = target.closest('[data-cart-remove]');
    if (removeBtn) {
      e.preventDefault();
      const item = removeBtn.closest('[data-cart-item]');
      if (!item) return;
      this._updateLine(item, 0);
      return;
    }
  }

  _readQuantity(item) {
    const el = item.querySelector('[data-cart-quantity-value]');
    if (!el) return 0;
    return parseInt((el.value || el.textContent || '').trim(), 10) || 0;
  }

  _findLineRow(drawer, itemKey) {
    if (!drawer || !itemKey) return null;
    const safe = cartDrawerEscapeKey(itemKey);
    return drawer.querySelector(`[data-cart-item][data-item-key="${safe}"]`);
  }

  async _updateLine(item, newQuantity) {
    const itemKey = item.getAttribute('data-item-key');
    if (!itemKey) return;
    if (item.dataset.updating === 'true') return;
    item.dataset.updating = 'true';

    // Directe feedback op het geklikte element; in de queue gebruiken we
    // opnieuw de live DOM (na eerdere swaps kan `item` gedisconnect zijn).
    this._setLineLoading(item, true);

    try {
      await CartDrawerItems._enqueueMutation(async () => {
        const drawer = document.querySelector('cart-drawer');
        this.cartDrawer = drawer || this.cartDrawer;

        const row = (drawer && this._findLineRow(drawer, itemKey)) || (item.isConnected ? item : null);
        if (row && row !== item) this._setLineLoading(item, false);
        if (row) this._setLineLoading(row, true);

        const sectionId = (this.cartDrawer && this.cartDrawer.sectionId) || 'cart-drawer';

        try {
          const url = `/cart/change.js?sections=${encodeURIComponent(sectionId)}`;
          const { res, text } = await CartDrawerItems._postCartChange(url, {
            id: itemKey,
            quantity: newQuantity,
          });

          let data = null;
          try {
            data = text ? JSON.parse(text) : null;
          } catch (_) {
            data = null;
          }

          const okPayload =
            res.ok && data && typeof data === 'object' && (Array.isArray(data.items) || data.sections);

          if (!okPayload) {
            const clearRow = (drawer && this._findLineRow(drawer, itemKey)) || row;
            this._handleCartMutationError(res.status, data, text, clearRow);
            return;
          }

          const html = data.sections && data.sections[sectionId];
          if (html && this.cartDrawer) {
            // silent: geen directe cart-update → apps pollen niet tegelijk met onze change-call
            this.cartDrawer.applySectionHTML(html, { silent: true });
            CartDrawerItems._scheduleAppCartNotify({
              source: 'cart-drawer',
              sectionId,
            });
          } else if (this.cartDrawer) {
            await this.cartDrawer.refresh();
          }
        } finally {
          const liveDrawer = document.querySelector('cart-drawer');
          const clearRow = (liveDrawer && this._findLineRow(liveDrawer, itemKey)) || null;
          if (clearRow) this._setLineLoading(clearRow, false);
          else if (row && row.isConnected) this._setLineLoading(row, false);
        }
      });
    } catch (err) {
      console.error('[cart-drawer-items] update failed', err);
      this._showError('Er is een fout opgetreden bij het bijwerken van de winkelwagen.');
      this._setLineLoading(item, false);
      const drawer = document.querySelector('cart-drawer');
      const clearRow = drawer && this._findLineRow(drawer, itemKey);
      if (clearRow) this._setLineLoading(clearRow, false);
    } finally {
      delete item.dataset.updating;
      if (item.isConnected) this._setLineLoading(item, false);
    }
  }

  _handleCartMutationError(status, data, rawText, item) {
    let errorMsg = 'Er is een fout opgetreden bij het bijwerken van de winkelwagen.';
    if (data && typeof data === 'object') {
      if (data.description) errorMsg = data.description;
      else if (data.message) errorMsg = data.message;
    } else if (status === 403) {
      errorMsg =
        'Toegang geweigerd. Dat gebeurt soms bij meerdere acties tegelijk; wacht even of vernieuw de pagina.';
    } else if (status === 429) {
      errorMsg =
        'Te veel verzoeken. Wacht even en probeer opnieuw. Bij lokaal ontwikkelen helpt soms de preview-URL (druk p in de terminal).';
    } else if (status === 422) {
      errorMsg = 'Je kunt niet meer artikelen toevoegen dan beschikbaar zijn.';
    } else if (rawText && rawText.trimStart().startsWith('<!')) {
      errorMsg = 'De server stuurde een foutpagina i.p.v. cart-data. Vernieuw de pagina en probeer opnieuw.';
    }
    this._showError(errorMsg);
    if (item && item.isConnected) this._setLineLoading(item, false);
  }

  _setLineLoading(item, loading) {
    item.classList.toggle('is-updating', loading);
    if (loading) {
      item.setAttribute('aria-busy', 'true');
    } else {
      item.removeAttribute('aria-busy');
    }
    item.querySelectorAll('button').forEach((b) => {
      b.disabled = loading;
    });
  }

  _showError(message) {
    if (typeof window.SpiruToast?.show === 'function') {
      window.SpiruToast.show(message);
    } else {
      console.warn('[cart-drawer-items]', message);
    }
  }
}

if (!customElements.get('cart-drawer-items')) {
  customElements.define('cart-drawer-items', CartDrawerItems);
}
